import { Box, Grid, HStack, Progress, Table, Tbody, Td, Text, Tr, VStack } from "@chakra-ui/react";
import type { CaregiverId } from "../../../../../shared/schema/schema";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import { formatCaregiverProfileValue } from "../../../caregiverProfileModal.utils";
import { useCaregiverProfileAvailability } from "./CaregiverProfileAvailabilitySection.hooks";
import {
  formatCaregiverAvailabilityDay,
  formatCaregiverAvailabilityRange,
  getAvailabilityProgressPercent,
} from "./caregiverAvailability.utils";

type Props = {
  caregiverId: CaregiverId;
};

function AvailabilityMetric(props: { label: string; maxValue: number; value: number | null }) {
  return (
    <Box w="100%">
      <HStack alignItems="baseline" justifyContent="space-between" mb="8px">
        <Text color={entityProfileModalColors.text} fontSize="14px" fontWeight={400}>
          {props.label}
        </Text>
        <Text color={entityProfileModalColors.text} fontSize="12px">
          {formatCaregiverProfileValue(props.value)}
        </Text>
      </HStack>
      <Progress
        bg="blue.100"
        borderRadius="2px"
        h="4px"
        sx={{
          "& > div": {
            bg: entityProfileModalColors.active,
          },
        }}
        value={getAvailabilityProgressPercent(props.value, props.maxValue)}
      />
    </Box>
  );
}

function AvailabilityStatusDot(props: { isBusy: boolean }) {
  return (
    <Box
      bg={props.isBusy ? entityProfileModalColors.danger : entityProfileModalColors.active}
      borderRadius="full"
      h="9px"
      w="9px"
    />
  );
}

export default function CaregiverProfileAvailabilitySection(props: Props) {
  const availabilityQuery = useCaregiverProfileAvailability(props.caregiverId);
  const availability = availabilityQuery.data?.caregiverAvailability;

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load caregiver availability."
      isError={availabilityQuery.isError}
      isLoading={availabilityQuery.isLoading}
      loadingLabel="Loading caregiver availability"
      onRetry={() => void availabilityQuery.refetch()}
    >
      {availability === undefined || availability === null ? null : (
        <Grid
          columnGap="30px"
          maxW="1040px"
          rowGap={8}
          templateColumns={{ base: "minmax(0, 1fr)", xl: "480px 480px" }}
          w="100%"
        >
          <Box maxW="480px" pt="12px" w="100%">
            <Table size="sm" variant="simple">
              <Tbody>
                {availability.dayAvailabilities.map((dayAvailability) => (
                  <Tr key={String(dayAvailability.day)}>
                    <Td borderColor="#eee" px="18px" py="9px" w="34px">
                      <AvailabilityStatusDot isBusy={dayAvailability.busyAllDay} />
                    </Td>
                    <Td
                      borderColor="#eee"
                      color={entityProfileModalColors.text}
                      fontSize="12px"
                      py="9px"
                      w="95px"
                    >
                      {formatCaregiverAvailabilityDay(dayAvailability.day)}
                    </Td>
                    <Td
                      borderColor="#eee"
                      color={entityProfileModalColors.text}
                      fontSize="12px"
                      py="9px"
                    >
                      {dayAvailability.busyAllDay
                        ? "Busy all day"
                        : formatCaregiverAvailabilityRange(
                            dayAvailability.startHour,
                            dayAvailability.endHour
                          )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <VStack alignItems="stretch" maxW="480px" pt="24px" spacing="22px" w="100%">
            <AvailabilityMetric
              label="Maximum travel distance"
              maxValue={20}
              value={availability.maxDistance}
            />
            <AvailabilityMetric
              label="Minimum visit duration"
              maxValue={20}
              value={availability.minDuration}
            />
            <AvailabilityMetric
              label="Maximum visit duration"
              maxValue={24}
              value={availability.maxDuration}
            />
          </VStack>
        </Grid>
      )}
    </EntityProfileQuerySection>
  );
}
